import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { getArticlesBySection, formatDate, formatViews } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { Article } from "@/types/content";

const PAGE_SIZE = 6;

export default function Industry() {
  const all = getArticlesBySection("industry");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "汽车资讯网 - 行业动态",
    description:
      "聚焦汽车行业政策法规、产销数据、技术趋势与产业链动态，帮助行业观察者与专业用户掌握市场走向。",
    keywords: "汽车行业动态,汽车政策,汽车市场,汽车产业,行业趋势",
  });

  useEffect(() => {
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const items: Article[] = all.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const featured = all.slice(0, 4);

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "行业动态" }]} />

      <div className="mt-4 bg-primary-950 rounded-lg p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 font-semibold mb-3">
          <i className="ri-line-chart-line" />
          行业观察
        </span>
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-background-50">
          行业动态
        </h1>
        <p className="mt-3 text-sm md:text-base text-background-200 max-w-3xl leading-relaxed">
          从政策法规到产销数据，从技术路线到出海布局，行业动态帮助你理解汽车产业背后的趋势与逻辑，为专业判断提供参考。
        </p>
      </div>

      <section className="mt-8">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-1.5 h-7 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl text-foreground-950">
            推荐专题
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map((item) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="h-36 overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title}配图`}
                  title={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {item.title}
                </h3>
                <span className="block mt-2 text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {formatDate(item.date)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-8 bg-background-50 border border-background-200 rounded-lg">
        <div className="px-4 md:px-6 py-3 border-b border-background-200 flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg text-foreground-950">
            动态列表
          </h2>
          <span className="text-sm text-foreground-600">
            共 <strong className="text-primary-700">{all.length}</strong> 条
          </span>
        </div>

        <div className="p-4 md:p-6">
          {items.length > 0 ? (
            <ul className="flex flex-col">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className="py-4 border-b border-background-200 last:border-b-0 flex items-start gap-4"
                >
                  <span className="hidden sm:flex w-9 h-9 shrink-0 rounded-md bg-secondary-100 text-secondary-800 items-center justify-center font-heading font-semibold">
                    {(safePage - 1) * PAGE_SIZE + index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 cursor-pointer"
                    >
                      <h3 className="text-sm md:text-base text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2 min-w-0">
                        {item.title}
                      </h3>
                      <span className="shrink-0 text-xs text-foreground-500 flex items-center gap-3 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {formatDate(item.date)}
                        </span>
                        <span className="hidden md:flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {formatViews(item.views)}
                        </span>
                      </span>
                    </Link>
                    <p className="mt-1.5 text-sm text-foreground-600 line-clamp-1 hidden sm:block">
                      {item.summary}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="暂无行业动态"
              desc="当前没有可展示的行业动态内容，请稍后再来查看。"
            />
          )}

          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </div>
      </section>
    </div>
  );
}